import * as THREE from 'three';

/**
 * Numerieke verificatie van procedurele assets.
 *
 * De werkwijze uit AGENTS.md: parametertabel → bouwen → **cijfers controleren** → pas dan
 * naar een render kijken. Een screenshot laat zien of iets er goed uitziet; deze functies
 * bewijzen dat het asset zich in productie gedraagt — juiste maten, juiste origin,
 * scharnieren op hun as, geen NaN's, binnen budget.
 */

export interface Measurement {
  min: THREE.Vector3;
  max: THREE.Vector3;
  size: THREE.Vector3;
  center: THREE.Vector3;
}

/** Wereld-bounding box van een object en al zijn kinderen. */
export function measure(object: THREE.Object3D): Measurement {
  object.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(object);
  return {
    min: bounds.min.clone(),
    max: bounds.max.clone(),
    size: bounds.getSize(new THREE.Vector3()),
    center: bounds.getCenter(new THREE.Vector3()),
  };
}

/** Alle wereld-posities van de vertices van een object en zijn kinderen. */
export function worldVertices(object: THREE.Object3D): THREE.Vector3[] {
  object.updateMatrixWorld(true);
  const points: THREE.Vector3[] = [];
  const vertex = new THREE.Vector3();

  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const position = child.geometry.getAttribute('position');
    for (let i = 0; i < position.count; i++) {
      vertex.fromBufferAttribute(position, i).applyMatrix4(child.matrixWorld);
      points.push(vertex.clone());
    }
  });

  return points;
}

/** Totaal aantal driehoeken in een object en zijn kinderen. */
export function countTriangles(object: THREE.Object3D): number {
  let total = 0;
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const geometry = child.geometry;
    total += geometry.index ? geometry.index.count / 3 : geometry.getAttribute('position').count / 3;
  });
  return total;
}

/** Aantal aparte meshes — een ruwe indicatie van de draw calls die dit asset kost. */
export function countMeshes(object: THREE.Object3D): number {
  let total = 0;
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) total += 1;
  });
  return total;
}

/**
 * Grootste afwijking van spiegelsymmetrie over het YZ-vlak (x → −x).
 *
 * Voor elke vertex wordt de dichtstbijzijnde gespiegelde tegenhanger gezocht; de grootste
 * gevonden afstand is de symmetriedelta. Bij een correct gespiegeld asset is die vrijwel nul.
 */
export function symmetryDeltaX(object: THREE.Object3D): number {
  const points = worldVertices(object);
  if (points.length === 0) return 0;

  const mirrored = new THREE.Vector3();
  let worst = 0;

  for (const point of points) {
    mirrored.set(-point.x, point.y, point.z);
    let closest = Infinity;
    for (const candidate of points) {
      const distance = candidate.distanceToSquared(mirrored);
      if (distance < closest) {
        closest = distance;
        if (closest === 0) break;
      }
    }
    worst = Math.max(worst, Math.sqrt(closest));
  }

  return worst;
}

/** Zijn er NaN- of Infinity-vertices? Die slopen bounding boxes en raycasts stilletjes. */
export function hasInvalidVertices(object: THREE.Object3D): boolean {
  let invalid = false;
  object.traverse((child) => {
    if (invalid || !(child instanceof THREE.Mesh)) return;
    const position = child.geometry.getAttribute('position');
    const array = position.array as ArrayLike<number>;
    for (let i = 0; i < array.length; i++) {
      if (!Number.isFinite(array[i]!)) {
        invalid = true;
        return;
      }
    }
  });
  return invalid;
}

/** Zoek een pivot op naam. Gooit als hij ontbreekt — een typfout mag niet stil doorglippen. */
export function requirePivot(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) {
    const available = [...collectNames(root)].join(', ');
    throw new Error(`Pivot "${name}" niet gevonden. Beschikbaar: ${available}`);
  }
  return found;
}

/** Wereldpositie van een pivot, voor het toetsen van scharnierhoogtes. */
export function pivotWorldPosition(root: THREE.Object3D, name: string): THREE.Vector3 {
  root.updateMatrixWorld(true);
  return requirePivot(root, name).getWorldPosition(new THREE.Vector3());
}

function collectNames(root: THREE.Object3D): Set<string> {
  const names = new Set<string>();
  root.traverse((child) => {
    if (child.name) names.add(child.name);
  });
  return names;
}
